import {makeAutoObservable} from "mobx"
import {LeadingLicensePlateType, VdaDocumentType, VdaFormData} from "../../components/page/vda/VdaForm";
import {VdaCustomerItem} from "./VdaCustomerStore";
import {equals} from "../../utils";
import {api, catchApi} from "../../api";
import {
    VehicleDataAcquisitionDataDto,
    VehicleDataAcquisitionDocumentDto,
} from "../../api/vda";
import {validationStore} from "../ValidationStore";
import {VdaPlacementItem, vdaPlacementStore} from "./VdaPlacementStore";
import {sortDocuments, updateLicensePlates} from "./VdaUtils";

class PositionStore {
    private current: number = 0

    public reset() {
        this.current = 0
    }

    public next() {
        this.current = this.current + 1
        return this.current
    }
}

const positionStore = new PositionStore()

function generateUniqueDocumentId(): string {
    return crypto.randomUUID();
}

export function createEmptyDocument(type: VdaDocumentType) {
    switch (type) {
        case "COR_I":
            return createEmptyCor1Document()
        case "COR_II":
            return createEmptyCor2Document()
        case 'LICENSE_PLATE_FRONT':
            return createEmptyLicensePlateDocument(type)
        case 'LICENSE_PLATE_REAR':
            return createEmptyLicensePlateDocument(type)
        default:
            return createEmptyDefaultDocument(type)
    }
}

export function createEmptyDefaultDocument(type: VdaDocumentType): VehicleDataAcquisitionDocumentDto {
    return {
        type: type,
        position: positionStore.next(),
        documentId: generateUniqueDocumentId(),
    } as VehicleDataAcquisitionDocumentDto
}

export function createEmptyLicensePlateDocument(type: VdaDocumentType): VehicleDataAcquisitionDocumentDto {
    return {
        type: type,
        position: positionStore.next(),
        documentId: generateUniqueDocumentId(),
        licensePlate: ''
    } as VehicleDataAcquisitionDocumentDto
}

export function createEmptyCor1Document(): VehicleDataAcquisitionDocumentDto {
    return {
        type: 'COR_I',
        position: positionStore.next(),
        documentId: generateUniqueDocumentId(),
        corSerial: '',
        deregistrationDate: '',
        registrationDateFirst: '',
        registrationDateLatest: '',
        licensePlate: ''
    } as VehicleDataAcquisitionDocumentDto
}

export function createEmptyCor2Document(): VehicleDataAcquisitionDocumentDto {
    return {
        type: 'COR_II',
        position: positionStore.next(),
        documentId: generateUniqueDocumentId(),
        corNumber: '',
        registrationDateFirst: '',
        registrationDateLatest: '',
        licensePlate: ''
    } as VehicleDataAcquisitionDocumentDto
}

class VdaCreateStore {

    get vinCheckDigit(): string | undefined {
        return this._vinCheckDigit;
    }

    set vinCheckDigit(value: string | undefined) {
        this._vinCheckDigit = value;
    }
    get licensePlate(): string {
        return this._licensePlate;
    }

    set licensePlate(value: string) {
        this._licensePlate = value;
    }
    get placement(): VdaPlacementItem | undefined {
        return this._placement;
    }

    set placement(value: VdaPlacementItem | undefined) {
        this._placement = value;
    }

    private get initData(): VdaFormData | undefined {
        return this._initData;
    }

    private set initData(value: VdaFormData | undefined) {
        this._initData = value;
    }

    get customer(): VdaCustomerItem | undefined {
        return this._customer;
    }

    set customer(value: VdaCustomerItem | undefined) {
        vdaPlacementStore.init(value?.id).finally()
        if (!value) {
            this.placement = undefined
        }
        this._customer = value;
    }

    get documents(): VehicleDataAcquisitionDocumentDto[] {
        return this._documents;
    }

    set documents(value: VehicleDataAcquisitionDocumentDto[]) {
        this._documents = value;
    }

    get vin(): string {
        return this._vin;
    }

    set vin(value: string) {
        this._vin = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    set valid(value: boolean) {
        this._valid = value;
    }

    get creating(): boolean {
        return this._creating;
    }

    set creating(value: boolean) {
        this._creating = value;
    }

    get dirty(): boolean {
        return !equals(this.formData, this.initData);
    }

    get leadingLicensePlate() : LeadingLicensePlateType | undefined {
        const availableTypes = this.documents.map(d => d.type)
        const priorityOrder: LeadingLicensePlateType[] = ['COR_I', 'COR_II', 'LICENSE_PLATE_FRONT', 'LICENSE_PLATE_REAR']
        return priorityOrder.find(type => availableTypes.includes(type))
    }

    private _vin: string = ''
    private _vinCheckDigit: string | undefined
    private _licensePlate: string = ''
    private _customer: VdaCustomerItem | undefined
    private _placement: VdaPlacementItem | undefined
    private _documents: VehicleDataAcquisitionDocumentDto[] = []

    private _creating: boolean = false
    private _valid: boolean = false

    private _initData: VdaFormData | undefined

    constructor() {
        makeAutoObservable(this)
    }

    get formData(): VdaFormData {
        const {
            vin,
            licensePlate,
            documents,
            placement,
            customer,
            vinCheckDigit
        } = this
        return {
            vin: vin,
            licensePlate,
            placement,
            documents,
            customer,
            vinCheckDigit
        }
    }

    set formData(data: VdaFormData) {
        const {
            vin,
            licensePlate,
            documents,
            placement,
            customer,
            vinCheckDigit
        } = data
        const updated = this.licensePlate !== licensePlate
        this.licensePlate = licensePlate
        updateLicensePlates(documents, updated, {leadingLicensePlate: this.leadingLicensePlate, licensePlate: this.licensePlate})
        this.vin = vin
        this.vinCheckDigit = vinCheckDigit
        this.documents = sortDocuments(documents)
        this.placement = placement
        this.customer = customer
    }

    reset() {
        positionStore.reset()
        this.vin = ''
        this.vinCheckDigit = ''
        this.licensePlate = ''
        this.documents = []
        this.customer = undefined
        this.initData = this.formData
    }

    resetAndReuse() {
        positionStore.reset()
        this.vin = ''
        this.licensePlate = ''
        this.vinCheckDigit = ''
        this.documents = this.documents.map(doc => createEmptyDocument(doc.type))
        this.initData = this.formData
    }

    async create(): Promise<string | undefined> {
        const dto = this.buildDto()
        this.creating = true
        const response = await catchApi(async () => await api.vda.api.createVehicleDataAcquisition(dto))
        this.creating = false

        return response?.data.id
    }

    async validate() {
        const dto = this.buildDto()

        await validationStore.validate(api.vda.api.validateVehicleDataAcquisition, dto)

        this.valid = Boolean(validationStore.valid)
    }

    private buildDto(): VehicleDataAcquisitionDataDto {
        const {
            vin,
            documents,
            placement,
            customer,
            vinCheckDigit
        } = this
        return {
            vin: vin,
            placementProcessId: placement?.id!!,
            documents: documents,
            customerId: customer?.id!!,
            vinCheckDigit: vinCheckDigit
        }
    }
}

const vdaCreateStore = new VdaCreateStore()

export {
    vdaCreateStore
}
